import React, { useState, useRef, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Paper,
  TextField,
  Button,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tabs,
  Tab,
  Box,
  Slide,
  useMediaQuery,
  Container,
  Grid,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Alert,
  Divider,
  LinearProgress,
  Chip,
  Badge
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  Send as SendIcon,
  CloudUpload as CloudUploadIcon,
  Camera as CameraIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  Person as PersonIcon,
  Note as NoteIcon,
  Description as FormsIcon,
  AttachFile as AttachmentsIcon,
  VerifiedUser as EligibilityIcon,
  Paid as BillingIcon,
  PhotoCamera as PhotoIcon,
  Cancel as CancelIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  HighlightOff as CloseIcon,
  Save as SaveIcon,
  Description as DescriptionIcon,
  Check as CheckIcon
} from "@mui/icons-material";
import { styled, useTheme } from "@mui/material/styles";
import Webcam from "react-webcam";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  secureApi,
  AVATAR_URL,
  MEDICAL_BILLING_PERSON,
  API_URL,
  ADMIN,
  MEDICAL_ASSISTANT,
  CHRONIC_PAIN_PATIENT,
  PERSONAL_INJURY_PATIENT,
} from "../config";
import PatientInfo from "../components/patient/patientInfo";
import ContactInfo from "../components/patient/contactInfo";
import EmergencyContact from "../components/patient/emergencyContact";
import Employment from "../components/patient/employment";
import ConditionRelatedTo from "../components/patient/conditionRelatedTo";
import InsuranceLawyerInfo from "../components/patient/insuranceLawyerInfo";
import InsuranceInformation from "../components/patient/insuranceInformation";
import Notes from "../components/patient/notes";
import Forms from "../components/patient/forms";
import Attachments from "../components/patient/attachments";
import Eligibility from "../components/patient/eligibility";
import Billing from "../components/patient/billing";
import TopNav from "../components/topnav";
import { Context } from "../context";
import axios from "axios";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 24,
  },
  pageHeader: {
    marginBottom: 24,
  },
  avatar: {
    width: 200,
    height: 200,
    border: "4px solid #f5f5f5",
  },
  tabRoot: {
    marginTop: 24,
    marginBottom: 24,
  },
  tab: {
    fontWeight: "600",
    fontSize: "16px",
  },
  uploadButtons: {
    marginBottom: 16,
  },
  photoCard: {
    marginBottom: 16,
    position: "relative",
  },
  photoActions: {
    position: "absolute",
    bottom: 8,
    right: 8,
    zIndex: 10,
  },
  fileInfo: {
    marginBottom: 16,
  },
  actionButtons: {
    marginTop: 32,
    marginBottom: 16,
  },
  camera: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  cameraActions: {
    marginTop: 16,
    display: "flex",
    gap: 8,
    justifyContent: "center",
  },
  pageSection: {
    marginBottom: 24,
  },
  formSection: {
    marginBottom: 16,
  },
  tabPanel: {
    minHeight: "50vh",
  }
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const a11yProps = (index) => {
  return {
    id: `patient-tab-${index}`,
    "aria-controls": `patient-tabpanel-${index}`,
  };
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Patient = () => {
  const classes = useStyles();
  const { setLoggedIn, user } = useContext(Context);
  const { patient_id } = useParams();
  const defaultAvatarUrl = "../images/default_avatar.png";
  const [loading, setLoading] = useState(false);
  
  // Patient state
  const [patient, setPatient] = useState({
    clinic: "",
    prefix: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    suffix: "",
    date_of_accident: dayjs("1/1/2024"),
    time_place: "",
    gender: "",
    date_of_birth: null,
    marital_status: "",
    ssn: "",
    description_of_accident: "",
    type: CHRONIC_PAIN_PATIENT,
    identifier: "",
    referredBy: "",
  });

  const [contactInfo, setContactInfo] = useState({
    preferred_contact: "",
    contact_number: "",
    home_phone: "",
    email_address: "",
    primary_address: "",
  });

  const [emergencyContact, setEmergencyContact] = useState({
    contact_name: "",
    contact_number: "",
    contact_relation: "",
  });

  const [employment, setEmployment] = useState({
    employer: "",
    business_phone: "",
    business_email: "",
    work_address: "",
  });

  const [conditionRelatedTo, setConditionRelatedTo] = useState({
    accident_type: "",
    date_of_first_visit: null,
    nf3: "",
    nf2: "",
  });

  const [insuranceLawyerInfo, setInsuranceLawyerInfo] = useState({
    insurance_company: "",
    insurance_address: "",
    auto_policy_number: "",
    claim_adjustor: "",
    claim_adjustor_phone: "",
    extension: "",
    claim: "",
    workers_compensation: "",
    carrier_case: "",
    lawyer: "",
    lawyer_phone_number: "",
    lawyer_email_address: "",
    lawyer_address: "",
  });

  const [insuranceInformation, setInsuranceInformation] = useState({
    insurance_provider: "",
    policy_number: "",
    group_number: "",
    name_of_subscriber: "",
    botox_authorization: "",
    botox_authorization_validity_dates: "",
    date_of_birth_of_subscriber: "",
    relationship_to_the_patient: "",
    address_of_subscriber: "",
    phone_number_of_subscriber: "",
  });

  // Demographics processing and image capture
  const [demographics, setDemographics] = useState(null);
  const [demographicsInfo, setDemographicsInfo] = useState({});
  const [loadingDemographics, setLoadingDemographics] = useState(false);
  const [avatar, setAvatar] = useState(null);
  const avatarInput = useRef();
  const navigate = useNavigate();

  // UI state
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [submitable, setSubmitable] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [doubleInputConfirmModalOpen, setDoubleInputConfirmModalOpen] = useState(false);
  
  // Notes state
  const [painConsultNoteExist, setPainConsultNoteExist] = useState(false);
  const [painConsultNoteSigned, setPainConsultNoteSigned] = useState(false);
  const [telehealthConsultNoteExist, setTelehealthConsultNoteExist] = useState(false);

  // Camera handling
  const webcamRef = useRef(null);
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [captureImgSrc, setCaptureImgSrc] = useState(null);
  const [selectedCamera, setSelectedCamera] = useState("environment");

  // Forms
  const [consentForm, setConsentForm] = useState(null);
  const [hipaaForm, setHipaaForm] = useState(null);
  const [pregnancyWaiverForm, setPregnancyWaiverForm] = useState(null);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCancelModalOpen = () => {
    if ((user && user.role === MEDICAL_BILLING_PERSON) || painConsultNoteSigned)
      navigate("/");
    else setCancelModalOpen(true);
  };

  const handleCancelModalClose = () => {
    setCancelModalOpen(false);
  };

  const goToPatientTable = () => {
    navigate("/");
  };

  const goToProcedureNote = () => {
    window.scrollTo(0, 0);
    navigate(`/pain-consult-note?patient_id=${patient_id}`);
  };

  const createAnotherPatient = () => {
    window.location.href = `/patient`;
  };

  const handleDemograhpicsSelect = (event) => {
    const file = event.target.files[0];
    setDemographics(file);
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.includes("image")) {
      setAvatar(file);
    } else {
      setAvatar(null);
    }
  };

  const handleAvatarRemove = () => {
    avatarInput.current.value = null;
    setAvatar(null);
  };

  const handleDemographicsUpload = async () => {
    if (demographics == null) return;
    // Create FormData object
    const formData = new FormData();
    formData.append("image", demographics);

    setLoadingDemographics(true);
    axios
      .post(`${API_URL}/get_demographics`, formData, {
        headers: {
          "Control-Allow-Origin": "*",
          "Access-Control-Allow-Headers": "*",
          Authorization: `Bearer ${window.localStorage.getItem(
            "alex-med-token"
          )}`,
        },
      })
      .then((response) => {
        const demographics = response.data;
        setDemographicsInfo(demographics.demographicsInfo);
        setPatient({
          ...patient,
          ...demographics.patient,
          date_of_accident: dayjs(demographics.patient.date_of_accident),
          date_of_birth: dayjs(demographics.patient.date_of_birth),
        });
        setContactInfo({
          ...contactInfo,
          preferred_contact: demographics.contactInfo.preferred_contact,
          contact_number: demographics.contactInfo.contact_number,
          email_address: demographics.contactInfo.email_address,
          primary_address: demographics.contactInfo.primary_address,
        });
        setConditionRelatedTo({
          ...conditionRelatedTo,
          accident_type: demographics.conditionRelatedTo.accident_type,
          date_of_first_visit: dayjs(
            demographics.conditionRelatedTo.date_of_first_visit
          ),
          nf3: demographics.conditionRelatedTo.nf3,
          nf2: demographics.conditionRelatedTo.nf2,
        });
        setInsuranceLawyerInfo({
          ...insuranceLawyerInfo,
          insurance_company: demographics.insuranceLawyerInfo.insurance_company,
          insurance_address: demographics.insuranceLawyerInfo.insurance_address,
          auto_policy_number:
            demographics.insuranceLawyerInfo.auto_policy_number,
          claim_adjustor: demographics.insuranceLawyerInfo.claim_adjustor,
          claim_adjustor_phone:
            demographics.insuranceLawyerInfo.claim_adjustor_phone,
          extension: demographics.insuranceLawyerInfo.extension,
          claim: demographics.insuranceLawyerInfo.claim,
          workers_compensation:
            demographics.insuranceLawyerInfo.workers_compensation,
          carrier_case: demographics.insuranceLawyerInfo.carrier_case,
          lawyer: demographics.insuranceLawyerInfo.lawyer,
          lawyer_phone_number:
            demographics.insuranceLawyerInfo.lawyer_phone_number,
          lawyer_email_address:
            demographics.insuranceLawyerInfo.lawyer_email_address,
          lawyer_address: demographics.insuranceLawyerInfo.lawyer_address,
        });
        
        toast.success("Demographics information extracted successfully!");
        setLoadingDemographics(false);
      })
      .catch((e) => {
        console.log(e);
        if (e.response && e.response.status === 401) {
          toast.warn("Session has been expired. You need to login again!");
          setLoggedIn(false);
          navigate("/login");
        } else if (e.response) {
          toast.error(e.response.data.message);
        } else {
          toast.error("Error processing demographics information");
        }
        setLoadingDemographics(false);
      });
  };

  const handleCancel = () => {
    navigate("/");
  };

  const handleCameraDialogOpen = () => {
    setCameraDialogOpen(true);
  };

  const handleCameraDialogClose = () => {
    setCaptureImgSrc(null);
    setCameraDialogOpen(false);
  };

  const handleCaptureImage = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCaptureImgSrc(imageSrc);
  };

  const retake = () => {
    setCaptureImgSrc(null);
  };

  const toggleCamera = () => {
    setSelectedCamera(selectedCamera === "user" ? "environment" : "user");
  };

  const confirmCapture = () => {
    var arr = captureImgSrc.split(","),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[arr.length - 1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    const file = new File([u8arr], "capture.jpeg", { type: mime });
    setDemographics(file);
    handleCameraDialogClose();
    toast.info("Photo captured. Click 'Upload Demographics' to process.");
  };

  useEffect(() => {
    if (patient.type === CHRONIC_PAIN_PATIENT)
      setSubmitable(
        patient.clinic &&
          patient.first_name &&
          patient.last_name &&
          patient.date_of_birth &&
          patient.gender
      );
    else
      setSubmitable(
        patient.clinic &&
          patient.first_name &&
          patient.last_name &&
          patient.date_of_birth &&
          patient.date_of_accident &&
          patient.gender
      );
  }, [patient]);

  const handleSave = (doubleCheck = true) => {
    if (user && user.role === MEDICAL_BILLING_PERSON) {
      toast.error("You don't have permission to create or update patients!");
      return;
    }
    
    setLoading(true);
    
    const newPatient = {
      ...patient,
      date_of_accident: patient.date_of_accident.format("MM/DD/YYYY"),
      date_of_birth: patient.date_of_birth.format("MM/DD/YYYY"),
    };

    // Create FormData object
    const formData = new FormData();
    if (avatar) formData.append("avatar", avatar);
    formData.append("patientInfo", JSON.stringify(newPatient));
    formData.append("contactInfo", JSON.stringify(contactInfo));
    formData.append("emergencyContact", JSON.stringify(emergencyContact));
    formData.append("employment", JSON.stringify(employment));

    if (patient.type === PERSONAL_INJURY_PATIENT) {
      const newConditionRelatedTo = {
        ...conditionRelatedTo,
        date_of_first_visit:
          conditionRelatedTo.date_of_first_visit.format("MM/DD/YYYY"),
      };
      formData.append(
        "insuranceLawyerInfo",
        JSON.stringify(insuranceLawyerInfo)
      );
      formData.append(
        "conditionRelatedTo",
        JSON.stringify(newConditionRelatedTo)
      );
    } else {
      formData.append(
        "insuranceInformation",
        JSON.stringify(insuranceInformation)
      );
    }
    formData.append("doubleCheck", doubleCheck);
    formData.append("demographicsInfo", JSON.stringify(demographicsInfo));

    // Make a POST request to the backend using secureApi or fetch
    if (patient_id) {
      axios
        .put(`${API_URL}/patient/${patient_id}`, formData, {
          headers: {
            "Control-Allow-Origin": "*",
            "Access-Control-Allow-Headers": "*",
            Authorization: `Bearer ${window.localStorage.getItem(
              "alex-med-token"
            )}`,
          },
        })
        .then((response) => {
          toast.success("Patient data updated successfully!");
          window.scrollTo(0, 0);
          setLoading(false);
        })
        .catch((e) => {
          console.log(e);
          if (e.response && e.response.status === 401) {
            toast.warn("Session has been expired. You need to login again!");
            setLoggedIn(false);
            navigate("/login");
          } else if (e.response) {
            toast.error(e.response.data.message);
          } else {
            toast.error("Error updating patient information");
          }
          setLoading(false);
        });
    } else {
      if (doubleCheck) {
        secureApi(window.localStorage.getItem("alex-med-token"))
          .post(`/check_create_patient`, formData)
          .then((response) => {
            const { patient_id } = response.data;
            if (patient_id) {
              navigate(`/patient/${patient_id}`);
              setSuccessModalOpen(true);
            } else {
              setDoubleInputConfirmModalOpen(true);
            }
            setLoading(false);
          })
          .catch((e) => {
            console.log(e);
            if (e.response && e.response.status === 401) {
              toast.warn("Session has been expired. You need to login again!");
              setLoggedIn(false);
              navigate("/login");
            } else if (e.response) {
              toast.error(e.response.data.message);
            } else {
              toast.error("Error creating patient");
            }
            setLoading(false);
          });
      } else {
        secureApi(window.localStorage.getItem("alex-med-token"))
          .post(`/patient`, formData)
          .then((response) => {
            const { patient_id } = response.data;
            navigate(`/patient/${patient_id}`);
            setSuccessModalOpen(true);
            setLoading(false);
          })
          .catch((e) => {
            console.log(e);
            if (e.response && e.response.status === 401) {
              toast.warn("Session has been expired. You need to login again!");
              setLoggedIn(false);
              navigate("/login");
            } else if (e.response) {
              toast.error(e.response.data.message);
            } else {
              toast.error("Error creating patient");
            }
            setLoading(false);
          });
      }
    }
  };

  useEffect(() => {
    if (patient_id) {
      setLoading(true);
      secureApi(window.localStorage.getItem("alex-med-token"))
        .get(`/patient/${patient_id}`)
        .then((response) => {
          const { data: patientData } = response;
          const patientInfoData = {
            ...patientData.patientInfo,
            date_of_accident: dayjs(patientData.patientInfo.date_of_accident),
            date_of_birth: dayjs(patientData.patientInfo.date_of_birth),
          };
          setPatient({
            ...patientInfoData,
            identifier: patientData.identifier,
          });
          setContactInfo(patientData.contactInfo);
          setEmergencyContact(patientData.emergencyContact);
          setEmployment(patientData.employment);

          if (patientInfoData.type === PERSONAL_INJURY_PATIENT) {
            const conditionRelatedToData = {
              ...patientData.conditionRelatedTo,
              date_of_first_visit: dayjs(
                patientData.conditionRelatedTo.date_of_first_visit
              ),
            };
            setConditionRelatedTo(conditionRelatedToData);
            setInsuranceLawyerInfo(patientData.insuranceLawyerInfo);
          } else {
            const dateOfBirthOfSubscriber =
              patientData.insuranceInformation.date_of_birth_of_subscriber;
            const insuranceInformationData = {
              ...patientData.insuranceInformation,
              date_of_birth_of_subscriber: dateOfBirthOfSubscriber
                ? dayjs(
                    patientData.insuranceInformation.date_of_birth_of_subscriber
                  )
                : null,
            };
            setInsuranceInformation(insuranceInformationData);
          }

          if (patientData.avatar) {
            const url = `${AVATAR_URL}/${patientData.avatar}`;
            fetch(url)
              .then((response) => response.blob())
              .then((blob) => {
                const file = new File([blob], patientData.avatar, {
                  type: blob.type,
                });
                setAvatar(file);
              })
              .catch((error) => {
                console.error("Error:", error);
              });
          }
          setConsentForm(patientData.consentForm);
          setHipaaForm(patientData.hipaaForm);
          setPregnancyWaiverForm(patientData.pregnancyWaiverForm);
          setLoading(false);
        })
        .catch((e) => {
          console.log(e);
          if (e.response && e.response.status === 401) {
            toast.warn("Session has been expired. You need to login again!");
            setLoggedIn(false);
            navigate("/login");
          } else if (e.response && e.response.data) {
            toast.error(e.response.data.message);
          } else {
            toast.error(
              "Something went wrong while getting patient information."
            );
          }
          setLoading(false);
        });
    } else {
      if (!user || user.role === MEDICAL_BILLING_PERSON) {
        toast.warn("You don't have permission to create a new patient!");
        navigate("/");
      }
    }
  }, [patient_id, navigate, setLoggedIn, user]);

  const renderPatientProfile = () => (
    <Box className={classes.formSection}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <ContactInfo
            contactInfo={contactInfo}
            setContactInfo={setContactInfo}
            painConsultNoteSigned={painConsultNoteSigned}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <EmergencyContact
            emergencyContact={emergencyContact}
            setEmergencyContact={setEmergencyContact}
            painConsultNoteSigned={painConsultNoteSigned}
          />
        </Grid>
      </Grid>
      
      {patient.type === PERSONAL_INJURY_PATIENT ? (
        <>
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Description of Accident
              </Typography>
              <TextField
                variant="outlined"
                label="Detailed description of the accident"
                color="primary"
                size="medium"
                fullWidth
                multiline
                rows={5}
                value={patient.description_of_accident}
                onChange={(e) =>
                  setPatient({
                    ...patient,
                    description_of_accident: e.target.value,
                  })
                }
                disabled={(user && user.role === MEDICAL_BILLING_PERSON)}
              />
            </CardContent>
          </Card>
          
          <Box sx={{ mt: 3 }}>
            <InsuranceLawyerInfo
              insuranceLawyerInfo={insuranceLawyerInfo}
              setInsuranceLawyerInfo={setInsuranceLawyerInfo}
              painConsultNoteSigned={painConsultNoteSigned}
            />
          </Box>
        </>
      ) : (
        <Box sx={{ mt: 3 }}>
          <InsuranceInformation
            insuranceInformation={insuranceInformation}
            setInsuranceInformation={setInsuranceInformation}
            painConsultNoteSigned={painConsultNoteExist}
          />
        </Box>
      )}
      
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <Employment
            employment={employment}
            setEmployment={setEmployment}
            painConsultNoteSigned={painConsultNoteSigned}
          />
        </Grid>
        {patient.type !== PERSONAL_INJURY_PATIENT && (
          <Grid item xs={12} md={6}>
            <ConditionRelatedTo
              conditionRelatedTo={conditionRelatedTo}
              setConditionRelatedTo={setConditionRelatedTo}
              painConsultNoteSigned={painConsultNoteSigned}
            />
          </Grid>
        )}
      </Grid>
      
      <Box display="flex" justifyContent="center" className={classes.actionButtons}>
        <Button
          variant="outlined"
          color="error"
          onClick={handleCancelModalOpen}
          sx={{ mr: 2 }}
          startIcon={<CancelIcon />}
        >
          Cancel
        </Button>
        {user && user.role !== MEDICAL_BILLING_PERSON && (
          <Button
            variant="contained"
            disabled={!submitable || loading}
            onClick={handleSave}
            startIcon={<SaveIcon />}
          >
            Save Patient Information
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <TopNav item="patients" />
      {loading && <LinearProgress />}
      
      <Container maxWidth="xl" className={classes.root}>
        {/* Demographics Upload Controls */}
        {user &&
          user.role !== MEDICAL_BILLING_PERSON && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Upload Demographics Information
                </Typography>
                
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Stack direction="row" spacing={2} className={classes.uploadButtons}>
                      <Button
                        component="label"
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                      >
                        Select File
                        <VisuallyHiddenInput
                          type="file"
                          accept="image/*, application/pdf"
                          onChange={handleDemograhpicsSelect}
                        />
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<CameraIcon />}
                        onClick={handleCameraDialogOpen}
                      >
                        Take Photo
                      </Button>
                      <LoadingButton
                        onClick={handleDemographicsUpload}
                        loading={loadingDemographics}
                        disabled={!demographics}
                        endIcon={<SendIcon />}
                        loadingPosition="end"
                        variant="contained"
                        color="success"
                      >
                        <span>Upload Demographics</span>
                      </LoadingButton>
                    </Stack>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    {demographics && (
                      <Box className={classes.fileInfo}>
                        <Chip 
                          icon={<DescriptionIcon />} 
                          label={demographics.name} 
                          color="primary" 
                          variant="outlined"
                        />
                      </Box>
                    )}
                  </Grid>
                </Grid>
                
                <Alert severity="info">
                  Upload a photo of the demographics information form to automatically extract patient details.
                  You can select a file from your device or take a photo directly.
                </Alert>
              </CardContent>
            </Card>
          )}
        
        {/* Patient Header */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card className={classes.photoCard}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  user && user.role !== MEDICAL_BILLING_PERSON ? (
                    <IconButton
                      component="label"
                      sx={{ backgroundColor: '#1976d2', color: 'white' }}
                    >
                      <PhotoIcon fontSize="small" />
                      <VisuallyHiddenInput
                        type="file"
                        accept="image/*"
                        ref={avatarInput}
                        onChange={handleAvatarChange}
                      />
                    </IconButton>
                  ) : null
                }
              >
                <Avatar
                  src={avatar ? URL.createObjectURL(avatar) : defaultAvatarUrl}
                  className={classes.avatar}
                  alt={patient.first_name ? `${patient.first_name} ${patient.last_name}` : "Patient"}
                />
              </Badge>
                
                {avatar && user && user.role !== MEDICAL_BILLING_PERSON && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleAvatarRemove}
                    startIcon={<DeleteIcon />}
                    size="small"
                    sx={{ mt: 2 }}
                  >
                    Remove Photo
                  </Button>
                )}
                
                {patient_id && patient.identifier && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Chip 
                      label={`ID: ${patient.identifier}`} 
                      variant="outlined" 
                      color="primary"
                      size="small"
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={9}>
            <PatientInfo
              patient={patient}
              setPatient={setPatient}
            />
          </Grid>
        </Grid>
        
        {/* Tabs Navigation */}
        <Box className={classes.tabRoot}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            indicatorColor="primary"
            textColor="primary"
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              '& .MuiTab-root': {
                fontWeight: 'bold', 
                fontSize: '16px'
              } 
            }}
          >
            <Tab 
              label="Patient Profile" 
              {...a11yProps(0)} 
              icon={<PersonIcon />}
              iconPosition="start"
            />
            <Tab 
              label="Notes" 
              {...a11yProps(1)}
              disabled={patient_id ? false : true}
              icon={<NoteIcon />}
              iconPosition="start"
            />
            <Tab 
              label="Forms" 
              {...a11yProps(2)}
              disabled={patient_id ? false : true}
              icon={<FormsIcon />}
              iconPosition="start"
            />
            <Tab 
              label="Attachments" 
              {...a11yProps(3)}
              disabled={patient_id ? false : true}
              icon={<AttachmentsIcon />}
              iconPosition="start"
            />
            {user && (user.role === ADMIN || user.role === MEDICAL_ASSISTANT) && (
              <Tab 
                label="Eligibility" 
                {...a11yProps(4)}
                disabled={patient_id ? false : true}
                icon={<EligibilityIcon />}
                iconPosition="start"
              />
            )}
            <Tab 
              label="Billing" 
              {...a11yProps(5)}
              disabled={patient_id ? false : true}
              icon={<BillingIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Box>
        
        {/* Tab Panels */}
        <Box sx={{ mt: 3 }}>
          {/* Patient Profile Tab */}
          <div
            role="tabpanel"
            hidden={tabValue !== 0}
            id="patient-tabpanel-0"
            aria-labelledby="patient-tab-0"
            className={classes.tabPanel}
          >
            {renderPatientProfile()}
          </div>
          
          {/* Notes Tab */}
          <div
            role="tabpanel"
            hidden={tabValue !== 1}
            id="patient-tabpanel-1"
            aria-labelledby="patient-tab-1"
            className={classes.tabPanel}
          >
            <Notes
              painConsultNoteExist={painConsultNoteExist}
              setPainConsultNoteExist={setPainConsultNoteExist}
              telehealthConsultNoteExist={telehealthConsultNoteExist}
              setTelehealthConsultNoteExist={setTelehealthConsultNoteExist}
              setPainConsultNoteSigned={setPainConsultNoteSigned}
            />
          </div>
          
          {/* Forms Tab */}
          <div
            role="tabpanel"
            hidden={tabValue !== 2}
            id="patient-tabpanel-2"
            aria-labelledby="patient-tab-2"
            className={classes.tabPanel}
          >
            <Forms
              consentForm={consentForm}
              hipaaForm={hipaaForm}
              pregnancyWaiverForm={pregnancyWaiverForm}
            />
          </div>
          
          {/* Attachments Tab */}
          <div
            role="tabpanel"
            hidden={tabValue !== 3}
            id="patient-tabpanel-3"
            aria-labelledby="patient-tab-3"
            className={classes.tabPanel}
          >
            <Attachments />
          </div>
          
          {/* Eligibility Tab */}
          {user && (user.role === ADMIN || user.role === MEDICAL_ASSISTANT) && (
            <div
              role="tabpanel"
              hidden={tabValue !== 4}
              id="patient-tabpanel-4"
              aria-labelledby="patient-tab-4"
              className={classes.tabPanel}
            >
              <Eligibility />
            </div>
          )}
          
          {/* Billing Tab */}
          <div
            role="tabpanel"
            hidden={tabValue !== 5}
            id="patient-tabpanel-5"
            aria-labelledby="patient-tab-5"
            className={classes.tabPanel}
          >
            <Billing patient_id={patient_id} />
          </div>
        </Box>
      </Container>
      
      {/* Dialogs */}
      
      {/* Cancel Confirmation Dialog */}
      <Dialog
        fullScreen={fullScreen}
        open={cancelModalOpen}
        onClose={handleCancelModalClose}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <WarningIcon color="warning" sx={{ mr: 1 }} />
          Confirm Cancellation
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            If you cancel, you will lose all the patient information you entered so far.
            Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCancelModalClose}>
            No, Keep Editing
          </Button>
          <Button variant="contained" color="error" onClick={handleCancel}>
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Success Dialog */}
      <Dialog 
        fullScreen={fullScreen} 
        open={successModalOpen}
        PaperProps={{
          sx: {
            borderTop: '5px solid #4caf50'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <SuccessIcon color="success" sx={{ mr: 1 }} />
          Patient Created Successfully
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            The new patient has been created successfully. What would you like to do next?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={goToPatientTable}>
            Return to Patient List
          </Button>
          <Button variant="outlined" onClick={createAnotherPatient}>
            Create Another Patient
          </Button>
          <Button variant="contained" color="primary" onClick={goToProcedureNote}>
            Create Pain Consult Note
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Duplicate Patient Warning Dialog */}
      <Dialog 
        fullScreen={fullScreen} 
        open={doubleInputConfirmModalOpen}
        PaperProps={{
          sx: {
            borderTop: '5px solid #ff9800'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <WarningIcon color="warning" sx={{ mr: 1 }} />
          Potential Duplicate Patient
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            There's already a patient with this first name, last name, and date of birth.
            Do you really want to create a new patient with this information?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setDoubleInputConfirmModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setDoubleInputConfirmModalOpen(false);
              handleSave(false);
            }}
          >
            Create Anyway
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Camera Dialog */}
      <Dialog
        open={cameraDialogOpen}
        onClose={handleCameraDialogClose}
        fullScreen
        TransitionComponent={Transition}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {captureImgSrc ? "Review Photo" : "Take Photo"}
          </Typography>
          <IconButton onClick={handleCameraDialogClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent className={classes.camera}>
          {captureImgSrc ? (
            <Box sx={{ maxWidth: '100%', width: '100%', textAlign: 'center' }}>
              <img
                src={captureImgSrc}
                alt="Captured image"
                style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: '8px' }}
              />
            </Box>
          ) : (
            <Box sx={{ maxWidth: '100%', width: '100%', textAlign: 'center' }}>
              <Webcam
                ref={webcamRef}
                style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: '8px' }}
                screenshotFormat="image/jpeg"
                screenshotQuality={1.0}
                videoConstraints={{ facingMode: selectedCamera }}
              />
            </Box>
          )}
        </DialogContent>
        
        <DialogActions className={classes.cameraActions}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleCameraDialogClose}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          
          {captureImgSrc ? (
            <>
              <Button 
                variant="outlined" 
                onClick={retake}
                startIcon={<CameraIcon />}
              >
                Retake
              </Button>
              <Button 
                variant="contained" 
                color="success" 
                onClick={confirmCapture}
                startIcon={<CheckIcon />}
              >
                Use Photo
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={toggleCamera}
              >
                Switch Camera
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleCaptureImage}
                startIcon={<CameraIcon />}
              >
                Take Photo
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Patient;